import Box from "@mui/material/Box";
import React, {useEffect, useMemo, useState} from "react";
import StyledLink from "../../../shared/ui/LinkStyled";
import {getRomanNumber, niceUserName, sortUserName} from "../../../utils/functions";
import Icon from "../../../shared/ui/Icon";
import CustomDataTable from "../../../shared/tables/CustomDataTable";
import GenericButton from "../../../shared/ui/GenericButton";
import {useAuth} from "../../../hooks/useAuth";
import {APIUpdateClassroom} from "../api/classroom";
import FormField from "../../../shared/form/FormField";
import {APIGetStudents} from "../../users/api/students";
import useDataTable from "../../../hooks/useDataTable";
import CustomToolbar from "../../../shared/ui/CustomToolbar";

type Student = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    email_verified_at: string | null;
};

type Classroom = {
    id: string;
    name: string;
    grade: number;
    status: number;
    students: Student[];
    teachers: { id: number }[];
};

interface StudentsListProps {
    classroom: Classroom;
    updateClassroomHandler: (classroom: Classroom) => void;
}

type ConfirmCallback = () => void;

const STUDENTS_TABLE_COLUMNS = [
    {id: "last_name", label: "Nume și prenume", numeric: false, disablePadding: false, noWrap: true, sortable: true,},
    {id: "email", label: "E-mail", numeric: false, disablePadding: false, sortable: true,},
    {id: "email_verified_at", label: "Status", numeric: false, noWrap: true, disablePadding: false, sortable: false,},
    {id: "actions", label: "Acțiuni", numeric: true, noWrap: false, disablePadding: false, sticky: true},
];

export const EmailVerifiedIcon = ({verified}: { verified: boolean }) => (
    <Box pl={0.8} width="100%">
        <Icon
            icon={verified ? "fas circle-check" : "fas circle-xmark"}
            fixedWidth={true}
            sx={{
                fontSize: "1.5rem",
                color: verified ? "success.main" : "error.main",
            }}
        />
    </Box>
);

const StudentsList = ({classroom, updateClassroomHandler}: StudentsListProps) => {
    const {update} = useAuth();
    const [students, setStudents] = useState<Student[]>([]);

    const {
        loading,
        setLoading,
        page,
        orderBy,
        setOrderBy,
        order,
        setOrder,
    } = useDataTable("", "last_name", undefined, "");

    useEffect(() => {
        setLoading(true);
        setStudents(sortUserName(classroom.students, order));
        setLoading(false);
    }, [classroom.students, order]);

    const mapStudentToRow = (student: Student) => {

        return {
            last_name: (
                <StyledLink to={`/elevi/${student.id}`}>
                    {niceUserName(student.first_name, student.last_name, student.email)}
                </StyledLink>
            ),
            email: student.email,
            email_verified_at: (
                <EmailVerifiedIcon verified={student.email_verified_at !== null} />
            ),
            actions: (
                <Box display="flex" justifyContent="end">
                    <GenericButton type="delete-from-classroom" onClick={() => confirmDelete(student)}/>
                </Box>
            ),
        };
    };

    const rows = useMemo(() => {
        return students.map(mapStudentToRow);
    }, [students]);

    const handleError = (code: string) => {
        update("loading", {show: false});
        update("error", {show: true, code});
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const newOrder = isAsc ? "desc" : "asc";
        setOrder(newOrder);
        setOrderBy(property);
        setStudents((currentStudents) => sortUserName(currentStudents, newOrder));
    };

    const updateClassroomStudents = (
        updatedStudents: Student[],
        callback: ConfirmCallback,) => {
        const userIds = [
            ...classroom.teachers.map(({id}) => id),
            ...updatedStudents.map(({id}) => id),
        ];

        APIUpdateClassroom(classroom.id, {
            name: classroom.name,
            grade: classroom.grade,
            status: classroom.status,
            users: userIds
        })
            .then((response) => {
                if (response.data.success) {
                    // console.log("updateClassroom OK", response);
                    const updateClassroom = {
                        ...classroom,
                        ...{students: updatedStudents},
                    };
                    updateClassroomHandler(updateClassroom);
                    setStudents(sortUserName(updatedStudents, order));
                } else {
                    handleError("A002");
                }
            })
            .catch((err) => {
                // console.log("updateClassroom ERR", err);
                handleError("A001");
            })
            .finally(() => {
                callback();
            });
    }

    const addStudent = (student: Student, callback: ConfirmCallback) => {
        if (!student) return;

        const alreadyExists = classroom.students.some(({id}) => id === student.id);
        const updatedStudents = alreadyExists
            ? classroom.students
            : [...classroom.students, student];

        updateClassroomStudents(updatedStudents, callback);
    };

    const confirmAddStudent = () => {
        let student: Student;
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat user-plus",
            title: "Adaugă elev",
            size: "large",
            message: (
                <>
                    <Box sx={{border: "1px solid #ddd", borderRadius: "16px"}}>
                        <FormField
                            name="students"
                            type="autocomplete_field"
                            label="Caută elev..."
                            optionsAPI={APIGetStudents}
                            multiple={false}
                            filterOptions={(x: any) => x}
                            onChangeAutocomplete={(event, value) => (student = value)}
                        />
                    </Box>
                </>
            ),
            returnFunction: (callback: ConfirmCallback) => {
                addStudent(student, callback);
            },
            buttonYes: "Adaugă",
            buttonNo: "Închide",
        });
    };

    const deleteStudent = (student: Student, callback: ConfirmCallback) => {
        if (!student) return;

        const updatedStudents = classroom.students.filter(({id}) => id !== student.id);

        updateClassroomStudents(updatedStudents, callback);
    };

    const confirmDelete = (student: Student) => {
        update("confirm", {
            show: true,
            closable: true,
            icon: "fat trash-can",
            title: "Ești sigur că vrei să ștergi acest elev?",
            message: (
                <>
                    Elevul
                    <b> {niceUserName(student.first_name, student.last_name, student.email)}</b> din clasa
                    ({getRomanNumber(classroom.grade ? classroom.grade : 0)}) {classroom.name} va fi șters.<br/><br/>
                </>
            ),
            returnFunction: (callback: ConfirmCallback) => {
                deleteStudent(student, callback);
            },
            buttonYes: "Șterge",
            buttonNo: "Închide",
        });
    };

    return (
        <>
        <CustomToolbar>
            <GenericButton
                type="add-student"
                onClick={confirmAddStudent}
            />
        </CustomToolbar>
        <CustomDataTable
            loading={loading}
            columns={STUDENTS_TABLE_COLUMNS}
            rows={rows}
            rowsPerPage={students.length}
            page={page}
            totalRows={students.length}
            handleRequestSort={handleRequestSort}
            orderBy={orderBy}
            order={order}
            hasSearch={false}
        />
        </>
    )
}

export default StudentsList;